import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { GenericDialogComponent } from '@app/management/dialogs/generic-confirm/generic-confirm.component';
import { Appointment } from '@models/appointments/appointment';
import { Patient } from '@models/patient';
import { ConvergeCard } from '@models/payments/converge-card';
import { ConvergePaymentsService } from '@services/converge-payments.service';
import { FinanceService } from '@services/finance.service';
import { ScriptService } from '@services/script.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-converge-payment',
  templateUrl: './converge-payment.component.html',
  styleUrls: ['./converge-payment.component.less'],
})
export class ConvergePaymentComponent implements OnInit, OnDestroy {
  @Input() patient: Patient;
  @Input() cancellationFee: number;
  @Input() selectedPatientTokenId: number;
  @Input() requestCreditCard: boolean = false;
  @Input() showRequestCreditCardButton: boolean = false;
  @Input() appointment: Appointment;
  @Output() cardSelectedEvent: EventEmitter<ConvergeCard> = new EventEmitter<ConvergeCard>();
  @Output() requestCreditCardChangedEvent: EventEmitter<any> = new EventEmitter<any>();

  private _isDisabled: boolean = false;
  @Input() set isDisabled(val: boolean) {
    this._isDisabled = val;
  }
  get isDisabled() {
    return this._isDisabled;
  }

  @Input() minimumExpiryDate: Date = new Date(Date.now());

  token: string = '';
  txnStatus: string = '';
  txnResponse: string = '';

  PayWithConverge: any;

  convergeCards: ConvergeCard[];
  selectedConvergeCard: ConvergeCard;

  unsub: Subject<void> = new Subject<void>();
  loading = false;

  constructor(
    private scriptService: ScriptService,
    private convergePaymentsService: ConvergePaymentsService,
    private dialog: MatDialog,
    private financeService: FinanceService
  ) {}

  async ngOnInit() {
    try {
      await this.scriptService.loadScript('jQuery');
      await this.scriptService.loadScript('PayWithConverge');
    } catch (error) {
      console.error(error);
    }

    this.PayWithConverge = (window as any).PayWithConverge;
    if (!this.PayWithConverge) throw new Error('PayWithConverge.js failed to load properly');

    this.loading = true;
    this.convergePaymentsService
      .getPatientCards(this.patient.patientId)
      .pipe(takeUntil(this.unsub))
      .subscribe((cards) => {
        this.convergeCards = cards;
        if (this.selectedPatientTokenId) {
          this.selectedConvergeCard = this.convergeCards.find((c) => c.convergeTokenId === this.selectedPatientTokenId);
        }
        this.loading = false;
      });
  }

  initiateLightbox(): void {
    this.loading = true;
    const tokenRequest = {
      patientId: this.patient.patientId,
      ssl_first_name: this.patient.firstName,
      ssl_last_name: this.patient.lastName,
      ssl_email: this.patient.email,
      ssl_phone: this.patient.mobileNumber,
      ssl_amount: this.cancellationFee?.toString(),
    };
    this.convergePaymentsService.getSessionToken(tokenRequest).subscribe({
      next: (data) => {
        this.token = data.token;
        this.openLightbox();
      },
      error: (error) => {
        this.loading = false;
        console.error(error);
        this.dialog.open(GenericDialogComponent, {
          width: '300px',
          data: {
            showCancel: false,
            title: 'Card Error',
            content: error,
            confirmButtonText: 'Ok',
          },
        });
      },
    });
  }

  openLightbox(): void {
    const paymentFields = {
      ssl_txn_auth_token: this.token,
    };
    const callback = {
      onError: (error: string) => {
        this.loading = false;
        this.dialog.open(GenericDialogComponent, {
          width: '300px',
          data: {
            showCancel: false,
            title: 'Card Error',
            content: error,
            confirmButtonText: 'Ok',
          },
        });
      },
      onCancelled: () => {
        this.loading = false;
      },
      onDeclined: (response: any) => {
        this.loading = false;
        let errorMessage = `${response.errorCode} - ${response.errorName} - ${response.errorMessage}`;
        this.dialog.open(GenericDialogComponent, {
          width: '300px',
          data: {
            showCancel: false,
            title: 'Card Declined',
            content: errorMessage,
            confirmButtonText: 'Ok',
          },
        });
      },
      onApproval: (response: any) => {
        var responseJson = JSON.stringify(response, null, '\t');
        this.convergePaymentsService.savePaymentInfo(this.patient.patientId, response).subscribe({
          next: (card) => {
            this.loading = false;
            // Add the card to the cards collection and select it
            var index = this.convergeCards.findIndex((c) => c.convergeTokenId == card.patientTokenId);
            if (index < 0) {
              // Add and select new card
              this.convergeCards.push(card);
              this.selectedConvergeCard = card;
              this.cardSelectedEvent.emit(card);
            } else {
              // Select the card in the list already
              card = this.convergeCards[index];
              this.selectedConvergeCard = card;
              this.cardSelectedEvent.emit(card);
            }
          },
          error: (error) => {
            this.loading = false;
            console.error(error);
            this.dialog.open(GenericDialogComponent, {
              width: '300px',
              data: {
                showCancel: false,
                title: 'Card Add Error',
                content: error,
                confirmButtonText: 'Ok',
              },
            });
          },
        });
      },
    };

    this.PayWithConverge.open(paymentFields, callback);
  }

  onConvergeCardSelected(event: MatRadioChange) {
    this.requestCreditCard = false;
    this.requestCreditCardChangedEvent.emit(this.requestCreditCard);
    this.cardSelectedEvent.emit(event.value);
  }

  onRequestCreditCardChangedEvent(event) {
    this.requestCreditCard = !this.requestCreditCard;
    if (this.requestCreditCard) {
      this.selectedConvergeCard = null;
      this.selectedPatientTokenId = null;
    }
    this.requestCreditCardChangedEvent.emit(this.requestCreditCard);
  }

  compareExpiryToMinimum(card: ConvergeCard) {
    if (card.expiry == null) return true;
    const { month, year } = parseExpiry(card.expiry);
    return (
      year < this.minimumExpiryDate.getUTCFullYear() ||
      (year == this.minimumExpiryDate.getUTCFullYear() && month < this.minimumExpiryDate.getUTCMonth() + 1)
    );
  }

  openRequestNewCardModal() {
    this.financeService.requestCreditCardModal(this.patient?.patientId, this.appointment?.appointmentId);
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}

function parseExpiry(expiry: string): { month: number; year: number } {
  if (!/^\d{4}$/.test(expiry)) {
    throw new Error("Invalid expiry format. Expected 'MMYY'.");
  }

  const month = parseInt(expiry.slice(0, 2), 10);
  const year = 2000 + parseInt(expiry.slice(2, 4), 10);

  if (month < 1 || month > 12) {
    throw new Error('Invalid month in expiry.');
  }

  return { month, year };
}
